import { Checkbox } from "@fluentui/react";
import React from "react";
import { modelsCreate, modelsUpdate } from "../../../../api/api";
import { useExternalModels, useModels } from "../../../../state/api/ai/useModels";
import { useModelPrices } from "../../../../state/api/ai/useModelPrices";
import { CACHE_TTL, CacheTtlLabelMap, Doc, EFFORT, EffortLabelMap, fromThinkingMode, MIN_THINKING_BUDGET, Model, THINKING, ThinkingLabelMap, toThinkingMode } from "../../../../types";
import Button from "../../buttons/Button";
import Submit from "../../buttons/Submit";
import SearchableComboBox from "../../combobox/SearchableComboBox";
import FieldWithLabel from "../../formik/FieldWithLabel";
import Icon from "../../icons/Icon";
import { Dialog, DialogBody, DialogFooter, ModalForm } from "../../modal/ModalForm";
import TokenCounter from "../tokens/TokenCounter";
import { formatPrice } from "./ModelManagement";

interface Props {
  model?: Doc<Model>;
  /** Opens the dialog as "create" with `model`'s values prefilled instead of editing `model` itself. */
  duplicate?: boolean;
}

export default function EditModelButton(props: Props) {

  const { model, duplicate } = props;

  const trigger = duplicate
    ? <Button text="Duplicate" icon="copy" />
    : <Button text={model ? "Edit" : "New"} icon={model ? "pen" : "plus"} />;

  return (
    <Dialog
      dialog={<ModelEditForm {...props} />}
      trigger={trigger}
    />
  )
}

function ModelEditForm(props: Props) {

  const {
    model,
    duplicate
  } = props;

  // a duplicate starts from the original's values but is saved as a new model
  const editing = duplicate ? undefined : model;

  const initialLabel = duplicate
    ? `Duplicate of ${model?.label || model?.externalModelId}`
    : (model?.label ?? "");

  const title = duplicate
    ? "Duplicate Model"
    : (editing ? "Edit Model" : "New Model");

  const [thinkingMode, setThinkingMode] = React.useState<THINKING>(toThinkingMode(model?.thinking));

  const {
    reloadModels
  } = useModels();

  const {
    externalModels,
    loadingExternalModels
  } = useExternalModels();

  const {
    modelPrices,
    loadingModelPrices
  } = useModelPrices();

  return (
    <ModalForm
      title={title}
      description="Configure a custom model variant"
      initialValues={{
        label: initialLabel,
        price: model?.price ?? "",
        externalModelId: model?.externalModelId ?? "",
        directToolUseOnly: model?.directToolUseOnly ?? false,
        maxOutputTokens: model?.maxOutputTokens ?? 4096,
        effort: model?.effort ?? null,
        thinking: model?.thinking ?? null,
        maxThinkingBudget: model?.maxThinkingBudget ?? null,
        cacheTtl: model?.cacheTtl ?? CACHE_TTL.ONE_HOUR
      } satisfies Model}
      onSubmit={async (values, apiCall) => {
        const payload: Model = {
          ...values,
          maxOutputTokens: Number(values.maxOutputTokens),
          thinking: fromThinkingMode(thinkingMode),
          maxThinkingBudget: thinkingMode === THINKING.ENABLED && values.maxThinkingBudget
            ? Number(values.maxThinkingBudget)
            : null
        };

        const res = await apiCall(
          editing
            ? modelsUpdate({ ...payload, _id: editing._id })
            : modelsCreate(payload)
        );

        if (!res.success) {
          return false;
        }

        await reloadModels();
        return true;
      }}
    >
      {
        formik => {

          const eb = (e: EFFORT | null) => {
            const a = e === formik.values.effort;

            return (
              <Button
                variant={a ? "solid" : "subtle"}
                text={e ? EffortLabelMap[e] : "Default"}
                onClick={() => formik.setFieldValue("effort", e)}
              />
            )
          }

          const thb = (t: THINKING) => {
            const a = t === thinkingMode;

            return (
              <Button
                variant={a ? "solid" : "subtle"}
                text={ThinkingLabelMap[t]}
                onClick={() => {
                  setThinkingMode(t);

                  if (t !== THINKING.ENABLED) {
                    formik.setFieldValue("maxThinkingBudget", null);
                  }
                }}
              />
            )
          }

          const i = (l: string) => (
            <div className="d-flex flex-row align-items-center gap-1">
              <Icon icon="info-circle" size={10} />
              <span className="font-code font-xs">
                {l}
              </span>
            </div>
          )

          return (
            <>
              <DialogBody>
                <div className="card p-3 d-flex flex-column gap-2">
                  <h6 className="fw-bold">Model</h6>
                  <div className="d-flex flex-column w-100 gap-1">
                    <FieldWithLabel
                      label="Label"
                      placeholder="e.g. Sonnet 5 (low effort)"
                      name="label"
                    />
                    {i("This is what shows up when picking a model for a skill.")}
                  </div>
                  <SearchableComboBox
                    onChange={x => {
                      formik.setFieldValue("externalModelId", x?.id);

                      if (x?.max_tokens) {
                        formik.setFieldValue("maxOutputTokens", x.max_tokens);
                      }

                      formik.setFieldValue("directToolUseOnly", x?.id?.includes("haiku"))
                    }}
                    label="Anthropic Model"
                    cannotClearValue
                    highlightChecked
                    values={externalModels}
                    value={formik.values.externalModelId}
                    itemToId={x => x.id}
                    itemToString={x => x.display_name}
                    loading={loadingExternalModels}
                  />
                  <Checkbox
                    label="Can only call tools in direct mode"
                    checked={formik.values.directToolUseOnly}
                    onChange={(e, c) => formik.setFieldValue("directToolUseOnly", c)}
                  />
                  <div className="d-flex flex-column w-100 gap-1">
                    <FieldWithLabel
                      type="number"
                      name="maxOutputTokens"
                      label="Max. tokens per message"
                    />
                    <TokenCounter tokenCount={Number(formik.values.maxOutputTokens)} />
                  </div>
                </div>
                <div className="card p-3 d-flex flex-column gap-2">
                  <h6 className="fw-bold">Reasoning</h6>
                  <div className="d-flex flex-column gap-1">
                    <span className="form-label">Effort</span>
                    <div className="d-flex flex-row flex-wrap gap-2">
                      {eb(null)}
                      {eb(EFFORT.LOW)}
                      {eb(EFFORT.MEDIUM)}
                      {eb(EFFORT.HIGH)}
                      {eb(EFFORT.XHIGH)}
                      {eb(EFFORT.MAX)}
                    </div>
                    {i("Effort controls how much the model reasons and spends. Default leaves it to the provider (high).")}
                  </div>
                  <div className="d-flex flex-column gap-1">
                    <span className="form-label">Thinking</span>
                    <div className="d-flex flex-row flex-wrap gap-2">
                      {thb(THINKING.DEFAULT)}
                      {thb(THINKING.ENABLED)}
                      {thb(THINKING.DISABLED)}
                    </div>
                  </div>
                  {
                    thinkingMode === THINKING.ENABLED && (
                      <div className="d-flex flex-column w-100 gap-1">
                        <FieldWithLabel
                          type="number"
                          min={MIN_THINKING_BUDGET}
                          name="maxThinkingBudget"
                          label="Thinking budget (tokens)"
                        />
                        <TokenCounter tokenCount={Number(formik.values.maxThinkingBudget)} />
                        {i("A fixed budget only applies to older models. Current Claude models reason adaptively and ignore it. Must be at least 1,024 and below the max. tokens per message.")}
                      </div>
                    )
                  }
                </div>
                <div className="card p-3 d-flex flex-column gap-2">
                  <h6 className="fw-bold">Price</h6>
                  <div className="d-flex flex-column w-100 gap-1">
                    <SearchableComboBox
                      onChange={x => formik.setFieldValue("price", x?._id)}
                      label="Price per MTok"
                      cannotClearValue
                      highlightChecked
                      values={modelPrices}
                      value={formik.values.price}
                      itemToId={x => x._id}
                      itemToString={x => x.label ? `${x.label} — ${formatPrice(x)}` : formatPrice(x)}
                      loading={loadingModelPrices}
                    />
                    {i("Prices are managed on the Prices tab and shared by every model that uses them.")}
                  </div>
                  <div className="d-flex flex-column w-100 gap-1">
                    <SearchableComboBox
                      onChange={x => formik.setFieldValue("cacheTtl", x)}
                      label="Prompt caching"
                      cannotClearValue
                      highlightChecked
                      values={[CACHE_TTL.FIVE_MINUTES, CACHE_TTL.ONE_HOUR]}
                      value={formik.values.cacheTtl}
                      itemToId={x => x}
                      itemToString={x => CacheTtlLabelMap[x]}
                    />
                    {i("How long cached prompts stay valid. 5 minutes: writes cost 1.25x input, for skills whose requests follow each other quickly. 1 hour: writes cost 2x input, pays off when users pause between messages.")}
                  </div>
                </div>
              </DialogBody>
              <DialogFooter>
                <Submit />
              </DialogFooter>
            </>
          )
        }
      }
    </ModalForm>
  )
}
